import { parseCalendarUrl } from "@/components/calendars/calendar-url.ts";
import { hasCalendarFocus } from "@/components/calendars/calendarFocus.ts";
import { useIsSharedThread } from "@/hooks/useIsSharedThread.ts";
import { cn, resolveApiPath } from "@/lib/utils.ts";
import "highlight.js/styles/github-dark-dimmed.css";
import { Check, Copy } from "lucide-react";
import React, { type JSX } from "react";
import { type ExtraProps, defaultUrlTransform } from "react-markdown";
import { Link } from "wouter";
import { UserTaskAnswerButton } from "@/components/userTasks/UserTaskAnswerButton.tsx";
import { BUTTON_LINK_TITLE, resolveMessageLink } from "@/types/messageLinks.ts";
import {
  authorizedImagePath,
  BROWSER_FRAME_FALLBACK_ALT,
  type MessageImageSource,
  messageImagePath,
  resolveMessageImageSrc,
} from "@/shared/messageImageSrc.ts";
import {
  USER_TASK_ANSWER_LABELS,
  parseUserTaskAnswerHref,
  userTaskHref,
} from "@/types/userTaskLinks.ts";
import { AppButtonLink } from "./AppButtonLink.tsx";
import { Celebration } from "./Celebration.tsx";
import { useMarkdownContext } from "./context.tsx";
import { useAnimatedStreamText } from "./useAnimatedStreamText.tsx";

/**
 * react-markdown `urlTransform` for our rendered markdown. Preserves our own
 * link schemes — entity URNs (`urn:bb:`) and `action:` directives — verbatim so
 * the link dispatcher sees them intact; `defaultUrlTransform` would otherwise
 * blank them as unknown schemes. Everything else goes through
 * `defaultUrlTransform` for the usual http/mailto sanitization.
 */
export const urnLinkTransform = (url: string): string => {
  // Preserve any `urn:bb:` href (not just strict numeric-id ones) so a stray
  // non-canonical URN renders as its original link rather than being blanked to
  // a broken external href by `defaultUrlTransform`.
  if (url.startsWith("urn:bb:") || url.startsWith("action:")) return url;
  return defaultUrlTransform(url);
};

type MarkdownComponentProps<E extends keyof JSX.IntrinsicElements> = React.ComponentProps<E> &
  ExtraProps;

/**
 * HOC to be used for block level elements that may render text that we want to stream in.
 */
const createStreambleTextComponent = <ElName extends keyof JSX.IntrinsicElements>(
  ElementName: ElName,
) => {
  const MarkdownComponent = ({
    node: _node, // from ReactMarkdown, we want to remove it from `...props`
    children,
    ...props
  }: MarkdownComponentProps<ElName>): JSX.Element | null => {
    const { isStreaming } = useMarkdownContext();
    const animatedChildren = useAnimatedStreamText({ children, isStreaming: isStreaming ?? false });

    // @ts-expect-error - it's having a hard time with the generic
    return <ElementName {...props}>{animatedChildren}</ElementName>;
  };

  MarkdownComponent.displayName = `${ElementName}Markdown`;
  return MarkdownComponent;
};

/**
 * Components for elements that we want to stream text in for. Should only include block
 * level elements that might directly render text.
 *
 * Inline elements aren't included because typically by the time we know to render one, its parent
 * has already streamed out all of its contents, and we don't want to double animate it. E.g. if
 * we already rendered `hi *my fri`, when we get `end*, how`, we would stream in the full first chunk raw.
 * Our chunks are controlled on the client side and are relatively small.
 */
const customStreamableIntrinsics = {
  p: createStreambleTextComponent("p"),
  h1: createStreambleTextComponent("h1"),
  h2: createStreambleTextComponent("h2"),
  h3: createStreambleTextComponent("h3"),
  h4: createStreambleTextComponent("h4"),
  h5: createStreambleTextComponent("h5"),
  h6: createStreambleTextComponent("h6"),
  blockquote: createStreambleTextComponent("blockquote"),
  li: createStreambleTextComponent("li"),
  // TODO: support code. Right that outputs a bunch of spans and such that we'd need to handle.
};

/**
 * Renders a link that interacts with the app (navigation or sidebar).
 *
 * Presentation of a canonical entity path is decided entirely by the enclosing
 * navigation boundaries (`CanvasNavBoundary` / `CalendarFocusNavBoundary` /
 * `SidebarNavBoundary`), which intercept the `<a href>` — this component never
 * classifies the surface. The one case no boundary covers is `#hash` in-page
 * scrolling, which is ordinary link behavior.
 */
const GenericAppLink = ({ appHref, children }: { appHref: string; children: React.ReactNode }) => {
  const { isStreaming } = useMarkdownContext();

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (appHref.startsWith("#")) {
        event.preventDefault();
        const elementId = appHref.slice(1);
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        } else {
          console.warn(`Element with ID "${elementId}" not found for scroll.`);
        }
      }
    },
    [appHref],
  );

  return (
    <Link
      className={cn(
        "link",
        // use the animation class directly, `StreamHandler` won't produce chunks within its text so useAnimatedStreamText is unnecessary
        isStreaming ? "animate-stream-text-enter" : "",
      )}
      href={appHref}
      onClick={handleClick}
    >
      {children}
    </Link>
  );
};

/** A link's text when it is plain text, or null when it holds markup. */
function textOf(children: React.ReactNode): string | null {
  if (typeof children === "string") return children;
  if (Array.isArray(children) && children.every((c) => typeof c === "string")) {
    return children.join("");
  }
  return null;
}

/**
 * Renders a markdown link according to what {@link resolveMessageLink} says it
 * is. The scheme table itself is shared with native, email, and SMS — this
 * component decides only how web presents the answer.
 *
 * Presentation of a canonical entity path is then decided by the enclosing
 * navigation boundaries (`CanvasNavBoundary` / `CalendarFocusNavBoundary` /
 * `SidebarNavBoundary`), which intercept the `<a href>`; this component never
 * classifies the surface.
 */
const CustomMarkdownLink = ({
  href = "",
  title,
  children,
  node: _node, // from ReactMarkdown, we want to remove it from `...props`
  ...props
}: MarkdownComponentProps<"a">) => {
  const { isStreaming, untrusted } = useMarkdownContext();
  const isSharedThread = useIsSharedThread();
  const resolved = resolveMessageLink(href);

  switch (resolved.kind) {
    case "app": {
      // A shared thread's artifact links carry their read-only state, but only
      // when the agent addressed the artifact by URN — an author who wrote the
      // path by hand already said what they meant about the query string.
      const appHref =
        isSharedThread && href.startsWith("urn:") && resolved.path.startsWith("/artifacts/")
          ? `${resolved.path}?shared=true`
          : resolved.path;

      // The one route whose validity the resolver can't judge: a `/calendar?…`
      // focus link carries its whole payload in the query string, and parsing
      // that lives in `calendars/calendar-url.ts` rather than in shared types.
      if (appHref.startsWith("/calendar?")) {
        const parts = parseCalendarUrl(appHref);
        if (!parts || !hasCalendarFocus(parts)) return null;
      }

      // A brief's one-tap answer to a task. It answers only when the link
      // reads as that answer: a task title linked to `?answer=done` by mistake
      // opens the task instead of closing it. And only in the owner's thread,
      // in text okthx wrote — a shared thread's reader holds no such task, and
      // a saved note's author is not the one to answer it. Otherwise it opens
      // the task.
      const taskAnswer = parseUserTaskAnswerHref(appHref);
      if (taskAnswer) {
        const answers =
          !isSharedThread &&
          !untrusted &&
          textOf(children) === USER_TASK_ANSWER_LABELS[taskAnswer.answer];
        return answers ? (
          <UserTaskAnswerButton {...taskAnswer}>{children}</UserTaskAnswerButton>
        ) : (
          <GenericAppLink appHref={userTaskHref(taskAnswer.taskId)}>{children}</GenericAppLink>
        );
      }

      // A standalone call to action rather than a word in a sentence, asked for
      // by the link's own CommonMark title. Presentation only: the destination
      // is the same route either way, so the link stays a link.
      if (title === BUTTON_LINK_TITLE) {
        return <AppButtonLink appHref={appHref}>{children}</AppButtonLink>;
      }
      return <GenericAppLink appHref={appHref}>{children}</GenericAppLink>;
    }

    case "action":
      switch (resolved.action) {
        case "celebrate":
          // The link text is the message; the action is what happens around it.
          return <Celebration>{children}</Celebration>;
        default:
          // An action added to the resolver but not handled here renders as its
          // own text, rather than silently celebrating.
          return <>{children}</>;
      }

    case "anchor":
      return <GenericAppLink appHref={`#${resolved.hash}`}>{children}</GenericAppLink>;

    case "external":
      return (
        <a
          href={resolved.url}
          className={cn(
            "link",
            // use the animation class directly, `StreamHandler` won't produce chunks within its text so useAnimatedStreamText is unnecessary
            isStreaming ? "animate-stream-text-enter" : "",
          )}
          target="_blank"
          rel="noopener noreferrer"
          {...props}
        >
          {children}
        </a>
      );

    case "none":
      // The text was written to be read, so it survives; only the target that
      // resolves nowhere is dropped. Rendering an `https://<garbage>` anchor
      // instead is what used to send readers to a site that does not exist.
      return <>{children}</>;
  }
};

/**
 * Width we ask the browser-frame endpoint to render at. Roughly 2x the widest
 * the thumbnail is displayed, so it stays sharp on a retina screen without
 * shipping the full-size capture.
 */
const BROWSER_FRAME_PREVIEW_WIDTH = 800;

type InternalImageProps = {
  source: Extract<MessageImageSource, { kind: "browserFrame" | "fileArtifact" }>;
  alt: string;
} & React.ComponentProps<"img">;

/**
 * A picture kept behind the session: a browser screenshot in the thread, or a
 * file artifact the agent made or the user uploaded. Its own component so the
 * shared-thread hook runs only for these nodes — `CustomMarkdownImage` is
 * instantiated for every image in every markdown block, and subscribing all of
 * them to the location would re-render each one on any route change.
 *
 * The agent wraps a browser frame in a markdown link to the page it captured,
 * so the enclosing anchor is part of the feature: whatever renders here has to
 * be non-empty, or the link becomes an invisible focusable target with no
 * accessible name.
 */
const InternalImage = ({ source, alt, ...props }: InternalImageProps) => {
  const isSharedThread = useIsSharedThread();
  const { imageSrcs } = useMarkdownContext();
  const label = alt || (source.kind === "browserFrame" ? BROWSER_FRAME_FALLBACK_ALT : "Image");
  const authorized = authorizedImagePath(source, imageSrcs);

  // The owner's routes are session-authed, so a shared viewer would only get a
  // 401 and a broken-image icon. A shared document's own images come with paths
  // that authorize the share instead.
  if (isSharedThread && authorized === null) {
    return <span className="text-xs text-muted-foreground">{label}</span>;
  }

  const path = authorized ?? messageImagePath(source, BROWSER_FRAME_PREVIEW_WIDTH);
  if (path === null) return null;
  return (
    <img
      {...props}
      src={resolveApiPath(path)}
      alt={label}
      loading="lazy"
      decoding="async"
      // Markdown images are otherwise unstyled (there is no `.markdown img`
      // rule), so a 1280px capture would render at full width. Cap it to the
      // thumbnail the live preview uses, and keep the natural aspect.
      // `hover:opacity-90` reads as affordance for the enclosing link.
      className={cn(
        "max-h-48 w-auto max-w-full rounded-md border object-contain transition-opacity hover:opacity-90",
        props.className,
      )}
    />
  );
};

/**
 * Renders markdown images with proper src handling.
 *
 * The agent writes internal tokens (`/browser-frames/{threadId}-{frameId}`,
 * `urn:bb:file:{id}`) rather than API paths because message content is
 * immutable — the route can move without breaking old threads, and no expiring
 * signed URL is baked in. `resolveMessageImageSrc` is the one grammar both
 * clients read them with.
 */
const CustomMarkdownImage = ({
  src = "",
  alt = "",
  node: _node,
  ...props
}: MarkdownComponentProps<"img">) => {
  const source = resolveMessageImageSrc(src);
  switch (source.kind) {
    case "browserFrame":
    case "fileArtifact":
      return <InternalImage source={source} alt={alt} {...props} />;
    case "invalid":
      // A reserved source without a renderable branch is malformed or retired,
      // not external — rendering it as a URL would request the SPA origin and
      // show a broken image.
      return null;
    case "external":
      return <img src={source.url} alt={alt} {...props} />;
  }
};

/**
 * Fenced code block with a copy button — the shortest path from a command the
 * agent suggests to the user's console.
 *
 * The button rests dimmed rather than fully hidden: a copy affordance nobody can
 * see until they happen to hover the block is one nobody finds.
 *
 * The text is read off the rendered DOM at click time rather than from
 * `children`, so highlight.js's nested spans (and any mid-stream re-render)
 * can't desync what gets copied from what the user sees.
 */
const CodeBlockWithCopy = ({
  node: _node,
  children,
  className,
  ...props
}: MarkdownComponentProps<"pre">) => {
  const { isTextStillArriving } = useMarkdownContext();
  const preRef = React.useRef<HTMLPreElement>(null);
  const [copied, setCopied] = React.useState(false);
  const resetTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  React.useEffect(() => () => clearTimeout(resetTimerRef.current), []);

  const handleCopy = React.useCallback(async () => {
    const text = preRef.current?.textContent ?? "";
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy code block:", error);
    }
  }, []);

  return (
    <div className="group relative">
      <pre className={className} ref={preRef} {...props}>
        {children}
      </pre>
      {/* Withheld only while the block is still growing, so a copy can't hand
          the user a truncated command. */}
      {!isTextStillArriving && (
        <button
          type="button"
          onClick={() => void handleCopy()}
          aria-label={copied ? "Copied" : "Copy code"}
          title={copied ? "Copied" : "Copy code"}
          className="absolute right-2 top-2 rounded-md border border-white/15 bg-white/10 p-1.5 text-white/70 opacity-70 transition-opacity hover:bg-white/20 hover:text-white focus-visible:opacity-100 group-hover:opacity-100"
          data-statsig-name="markdown-code-block-copy"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      )}
    </div>
  );
};

/** All custom components for Markdown. */
export const customMarkdownComponents = {
  a: CustomMarkdownLink,
  img: CustomMarkdownImage,
  pre: CodeBlockWithCopy,
  ...customStreamableIntrinsics,
};
